/**
 * @file card-sub-components.tsx
 *
 * Sub-components for MD3 Expressive Card layout anatomy:
 * - CardHeader: Avatar / Icon + Title + Subtitle + Action slot
 * - CardMedia: Responsive media region (image/video) with aspect ratio control
 * - CardContent: Main body text & descriptive content area
 * - CardFooter: Action buttons row with flexible alignment
 *
 * @see https://m3.material.io/components/cards/guidelines#anatomy
 */

import * as React from "react";
import { cn } from "../../../lib/utils";
import type {
	CardContentProps,
	CardFooterProps,
	CardHeaderProps,
	CardMediaProps,
} from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// CardHeader
// ─────────────────────────────────────────────────────────────────────────────
export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
	(
		{ className, avatar, title, subheader, action, children, ...props },
		ref,
	) => {
		return (
			<div
				ref={ref}
				className={cn(
					"flex items-center gap-4 px-6 pt-6 pb-2 relative z-1",
					className,
				)}
				{...props}
			>
				{avatar && <div className="shrink-0 flex items-center">{avatar}</div>}
				{title || subheader ? (
					<div className="flex-1 min-w-0">
						{title && (
							<div className="text-base font-medium text-m3-on-surface truncate">
								{title}
							</div>
						)}
						{subheader && (
							<div className="text-sm text-m3-on-surface-variant truncate">
								{subheader}
							</div>
						)}
					</div>
				) : null}
				{children}
				{action && (
					<div className="shrink-0 ml-auto flex items-center">{action}</div>
				)}
			</div>
		);
	},
);
CardHeader.displayName = "CardHeader";

// ─────────────────────────────────────────────────────────────────────────────
// CardMedia
// ─────────────────────────────────────────────────────────────────────────────
const ASPECT_RATIO_CLASSES = {
	video: "aspect-video",
	square: "aspect-square",
	wide: "aspect-[21/9]",
	auto: "",
} as const;

export const CardMedia = React.forwardRef<HTMLDivElement, CardMediaProps>(
	(
		{
			className,
			src,
			alt = "",
			aspectRatio = "video",
			component,
			children,
			...props
		},
		ref,
	) => {
		const ratioClass =
			aspectRatio in ASPECT_RATIO_CLASSES
				? ASPECT_RATIO_CLASSES[aspectRatio as keyof typeof ASPECT_RATIO_CLASSES]
				: "";

		const Component = component || (src && !children ? "img" : "div");

		return (
			<div
				ref={ref}
				className={cn(
					"relative w-full overflow-hidden shrink-0",
					ratioClass,
					className,
				)}
				{...props}
			>
				{children ? (
					children
				) : src ? (
					<Component
						src={src}
						alt={alt}
						className="w-full h-full object-cover"
					/>
				) : null}
			</div>
		);
	},
);
CardMedia.displayName = "CardMedia";

// ─────────────────────────────────────────────────────────────────────────────
// CardContent
// ─────────────────────────────────────────────────────────────────────────────
export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
	({ className, children, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"px-6 py-4 text-sm text-m3-on-surface-variant flex-1 relative z-1",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);
CardContent.displayName = "CardContent";

// ─────────────────────────────────────────────────────────────────────────────
// CardFooter
// ─────────────────────────────────────────────────────────────────────────────
const ALIGN_CLASSES = {
	start: "justify-start",
	center: "justify-center",
	end: "justify-end",
	between: "justify-between",
} as const;

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
	({ className, align = "end", children, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"flex items-center gap-2 px-6 pb-6 pt-2 relative z-1",
					ALIGN_CLASSES[align],
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);
CardFooter.displayName = "CardFooter";
